import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { finalize } from 'rxjs/operators';
import scrollIntoView from 'scroll-into-view-if-needed';

import { StaticInfoMenuType } from 'Angular/services/static-menus/types/static-menu-type.type';
import {
    StaticInfoFieldMenuCategory,
    StaticInfoFieldMenuItem,
} from 'Angular/services/static-menus/interfaces/static-info-field-menu.interface';
import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { StaticMenuService } from 'Angular/services/static-menus/static-menus.service';

/**
 * Info Field List Component
 *
 * Handles displaying the static menu of info fields and allows selection of one or multiple
 * info fields depending on the isSingleSelect input.
 *
 * Input Properties:
 *  infoType: 'company', 'fund', 'all', etc.
 *  modalOpen: Whether the parent modal is open. This component will only autoscroll to
 *             the selected info field if the modal is open.
 *  selectedInfoFields: array of selected info fields. Will only ever hold 1 element if isSingleSelect.
 *  isSingleSelect: Whether one or multiple info fields should be able to be selected at a time.
 *  defaultOnly: info needed for context aware data source filtering
 *
 * Output Properties:
 *  selectHandler: emits any info field that is clicked.
 */
@Component({ standalone: false, selector: 'ycn-info-field-list', templateUrl: './info-field-list.template.html' })
export class InfoFieldListComponent implements OnInit, OnChanges {
    @Input() infoType!: StaticInfoMenuType;

    @Input() modalOpen!: boolean;

    @Input() selectedInfoFields: StaticInfoFieldMenuItem[] = [];

    @Input() isSingleSelect: boolean = true;

    @Input() defaultOnly: boolean = false;

    @Output() selectHandler = new EventEmitter<StaticInfoFieldMenuItem>();

    // Categories can be passed in or fetched here

    @Input() categories: StaticInfoFieldMenuCategory[] = [];

    @Input() fetchCategories: boolean = true;

    // This is used to display the info fields for the selected category.
    categoryInfoFieldsMap: { [categoryName: string]: StaticInfoFieldMenuItem[] } = { '': [] };

    selectedCategoryName: string = '';

    // Used to show checkmarks for selected fields
    selectedInfoFieldsMap: { [key: string]: boolean } = {};

    readonly MENU_TYPE = 'infoFieldMenu';

    loading: boolean = false;

    constructor(private el: ElementRef, private staticMenuService: StaticMenuService) {}

    ngOnInit(): void {
        if (this.fetchCategories) {
            this.loading = true;
            this.staticMenuService
                .getStaticMenu(this.MENU_TYPE, this.infoType, this.defaultOnly)
                .pipe(
                    finalize(() => {
                        this.loading = false;
                    })
                )
                .subscribe((response) => {
                    this.categories = response;
                });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedInfoFields && !changes.selectedInfoFields.isFirstChange()) {
            // Mark all fields in the selectedInfoFields array as selected
            this.selectedInfoFieldsMap = this.selectedInfoFields.reduce(
                (acc, infoField) => ({
                    ...acc,
                    [infoField.key]: true,
                }),
                {}
            );
        }

        if (changes.modalOpen && !changes.modalOpen.isFirstChange()) {
            if (this.modalOpen) {
                // When the modal opens, scroll to the selected field if it is single select
                if (this.isSingleSelect && this.selectedInfoFields && this.selectedInfoFields.length) {
                    this.autoSelectInfoField({ search_id: this.selectedInfoFields[0].key }, false);
                } else {
                    // Select the first category by default otherwise
                    this.selectCategoryByName(this.categories[0].name);
                }
            } else {
                // Clear the selected category when the modal is closed
                this.selectedCategoryName = '';
            }
        }
        if (changes.categories) {
            this.categoryInfoFieldsMap = {};
        }
    }

    autoSelectInfoField(result: AutocompleteResult | { search_id: string }, emitField = true): void {
        let menuInfoField: StaticInfoFieldMenuItem | null = null;
        let menuCategory: StaticInfoFieldMenuCategory | null = null;
        // Find the info field and category in the static menu that match
        // the object selected in the autocomplete
        this.categories.some((category) => {
            const infoField = category.list_items.find((field) => field.key === result.search_id);
            if (infoField) {
                menuCategory = category;
                menuInfoField = infoField;
            }
            return !!infoField;
        });
        if (!menuInfoField) {
            return;
        }
        this.selectCategoryByName(menuCategory!.name);
        if (emitField) {
            this.selectHandler.emit(menuInfoField);
        }
        this.scrollTo(`[id='${(menuInfoField as StaticInfoFieldMenuItem)!.key}']`);
    }

    selectCategoryByName(categoryName: string): void {
        this.selectedCategoryName = categoryName;
        this.scrollTo(`div.linked-list-item-header[id="${this.selectedCategoryName}"]`);

        // If this category was selected before then we don't need
        // to update the map again with the array of info fields in the category
        if (this.categoryInfoFieldsMap[categoryName]) {
            return;
        }

        const selectedCategory: StaticInfoFieldMenuCategory | undefined = this.categories.find(
            (category) => category.name === categoryName
        );
        // Update the map so that we display the info fields in the selected category
        if (selectedCategory) {
            this.categoryInfoFieldsMap[categoryName] = selectedCategory.list_items;
        }
    }

    scrollTo(querySelector: string): void {
        setTimeout(() => {
            const selectedEl: Element | null = this.el.nativeElement.querySelector(querySelector);
            if (selectedEl) {
                scrollIntoView(selectedEl, {
                    block: 'center',
                    // do not scroll beyond the modal
                    boundary: (parent: Element) => !parent.classList.contains('modal'),
                    scrollMode: 'if-needed',
                });
            }
        });
    }
}
